import type {
    ModelAngleId,
    ModelDetailLevel,
    ModelGenerationMode,
    ModelJob,
    ModelJobStatus,
    ModelPhoto,
    ModelReviewStatus
} from '@/types'

// Shared settings for 3D models and the AI 3D generator (safe to import from client and server).

// Hand-uploaded GLB files only; the server rejects anything else and anything larger.
export const MAX_MODEL_MB = 50

// Photo limit before the browser compresses it (JPG, JPEG, PNG or WEBP).
export const MAX_PHOTO_MB = 10

export type ModelAngle = {
    id: ModelAngleId
    // Image column in model_jobs (with a <column>_alt companion).
    column: string
    label: string
    hint: string
    // A view of the whole doll the AI can use; the detail photo is documentation only.
    view: boolean
}

export const MODEL_ANGLES: Array<ModelAngle> = [
    { id: 'front', column: 'photo_front', label: 'Depan', hint: 'Wajah menghadap kamera, seluruh boneka terlihat.', view: true },
    { id: 'back', column: 'photo_back', label: 'Belakang', hint: 'Putar boneka 180 derajat dari tampak depan.', view: true },
    { id: 'left', column: 'photo_left', label: 'Samping kiri', hint: 'Sisi kiri boneka, putar 90 derajat.', view: true },
    { id: 'right', column: 'photo_right', label: 'Samping kanan', hint: 'Sisi kanan boneka, putar 90 derajat ke arah lain.', view: true },
    { id: 'top', column: 'photo_top', label: 'Atas', hint: 'Dari atas kepala, kamera sedikit miring.', view: true },
    { id: 'detail', column: 'photo_detail', label: 'Detail', hint: 'Dekat ke wajah, tekstur rajutan, atau aksesori.', view: false }
]

export const angleLabel = (id: ModelAngleId): string => MODEL_ANGLES.find(angle => angle.id === id)?.label ?? id

export const GENERATION_MODES: Array<{ id: ModelGenerationMode, label: string, description: string }> = [
    {
        id: 'single',
        label: 'Satu foto',
        description: 'Cukup satu foto tampak depan. AI (TripoSR) menebak sisi lain boneka, cepat dan cocok untuk boneka sederhana.'
    },
    {
        id: 'multiview',
        label: 'Multi-view',
        description: 'Foto dari beberapa sudut: depan, belakang, samping, atas, dan detail. Disiapkan untuk model AI multi-view.'
    }
]

export const isGenerationMode = (value: unknown): value is ModelGenerationMode => value === 'single' || value === 'multiview'

// The photo slots each mode shows: single mode uses only the main (front) photo.
export const anglesForMode = (mode: ModelGenerationMode): Array<ModelAngle> => (mode === 'single' ? MODEL_ANGLES.filter(angle => angle.id === 'front') : MODEL_ANGLES)

// Multi-view needs the front photo plus at least one other view of the whole doll.
export const MIN_MULTIVIEW_VIEWS = 2

// Checks the photos for a mode; the same rule runs in the browser and on the server.
export const photoProblem = (mode: ModelGenerationMode, photos: Partial<Record<ModelAngleId, string>>): string | null => {
    if (!photos.front) return mode === 'single' ? 'Unggah satu foto utama (tampak depan).' : 'Foto tampak depan wajib diunggah.'
    if (mode === 'single') return null

    const views = MODEL_ANGLES.filter(angle => angle.view && photos[angle.id]).length

    if (views < MIN_MULTIVIEW_VIEWS) return 'Mode multi-view butuh minimal satu foto lain selain depan (belakang, samping kiri, samping kanan, atau atas).'

    return null
}

// The photos of a job that the AI receives, front first (the detail photo is never sent).
export const photosForAi = (mode: ModelGenerationMode, photos: Array<ModelPhoto>): Array<ModelPhoto> => {
    return anglesForMode(mode)
        .filter(angle => angle.view)
        .map(angle => photos.find(photo => photo.angle === angle.id))
        .filter((photo): photo is ModelPhoto => Boolean(photo))
}

// How dense the surface is: the marching cubes resolution the AI backend uses for TripoSR.
export const DETAIL_LEVELS: Array<{ id: ModelDetailLevel, label: string, description: string, resolution: number }> = [
    { id: 'ringan', label: 'Ringan', description: 'Bentuk lebih sederhana dan file kecil. Proses tercepat.', resolution: 192 },
    { id: 'seimbang', label: 'Seimbang', description: 'Pilihan terbaik untuk sebagian besar boneka rajut.', resolution: 256 },
    { id: 'detail', label: 'Detail', description: 'Permukaan lebih halus untuk boneka dengan banyak lekukan. Proses lebih lama.', resolution: 320 }
]

export const JOB_STATUS_LABELS: Record<ModelJobStatus, string> = {
    pending: 'Menunggu',
    processing: 'Diproses',
    finalizing: 'Menyimpan hasil',
    succeeded: 'Selesai',
    failed: 'Gagal'
}

export const REVIEW_STATUS_LABELS: Record<ModelReviewStatus, string> = {
    draft: 'Draf',
    review: 'Menunggu persetujuan',
    approved: 'Disetujui',
    rejected: 'Ditolak',
    published: 'Tampil di etalase',
    replaced: 'Diganti model baru'
}

export const isJobActive = (job: Pick<ModelJob, 'status'>): boolean => job.status === 'pending' || job.status === 'processing' || job.status === 'finalizing'

export type ModelTabId = 'semua' | 'menunggu' | 'berhasil' | 'gagal' | 'terbit'

export const MODEL_TABS: Array<{ id: ModelTabId, label: string }> = [
    { id: 'semua', label: 'Semua Model 3D' },
    { id: 'menunggu', label: 'Menunggu Persetujuan' },
    { id: 'berhasil', label: 'Selesai Dibuat' },
    { id: 'gagal', label: 'Gagal' },
    { id: 'terbit', label: 'Sudah Diterbitkan' }
]

export const matchesModelTab = (job: Pick<ModelJob, 'status' | 'reviewStatus'>, tab: ModelTabId): boolean => {
    if (tab === 'menunggu') return job.status === 'succeeded' && job.reviewStatus === 'review'
    if (tab === 'berhasil') return job.status === 'succeeded'
    if (tab === 'gagal') return job.status === 'failed'
    if (tab === 'terbit') return job.reviewStatus === 'published'

    return true
}
